/* eslint-disable react/no-unused-prop-types */

import PropTypes from 'prop-types';
import React from 'react';
import { transforms, masks } from 'ctm-util-functions';

import { getHasSavedResults } from 'ctm-selectors/selectors/results';
import { CoverDetails } from './cover_details/cover_details';
import { OccupancyDetails } from './occupancy_details/occupancy_details';
import { DescriptionDetails } from './description_details/description_details';
import { CoverAmountsDetails } from './cover_amounts_details/cover_amounts_details';
import { PreviousInsurance } from './previous_insurance/previous_insurance';
import { PolicyHolderDetails } from './policy_holder_details/policy_holder_details';
import { CommencementDate } from './commencement_date/commencement_date';

const baseUrl = '/home-contents-insurance/journey';

const EditMyDetails = (props) => {
    /**
     * Replaces '_' with ' ' and capitalizes a string value
     * @param {String} value the string that you want to capitalize
     * @returns {String} returns a capitalized string
     */
    const capitalize = (value) => {
        if (!value) {
            return '';
        }

        return value[0].toUpperCase() + value.substring(1).toLowerCase();
    };

    const hasSavedResults = getHasSavedResults();

    // function for the sub-components to get the appropriate href
    // if the user has saved results, the href should be javascript:void(); to block the link navigation
    // Otherwise, the href should be the actual url which will be used by the link navigation
    const getHref = (url) => {
        if (hasSavedResults) {
            return 'javascript:void();';
        }
        return `${baseUrl}/${url}`;
    };

    // function for the sub-components to handle the onClick event
    // this runs in conjunction with the above getHref function
    // if the user has saved results, the nav link will be blocked
    // and these dispatches will open the quote modal and redirect the user to the appropriate page when confirmed
    const quoteModalOnClick = (path) => {
        const { dispatch } = props;

        if (hasSavedResults) {
            dispatch({
                type: 'SET_RESULTS_CONFIG_VALUE',
                key: 'helpers.updateQuoteModal',
                value: { open: true }
            });
            dispatch({
                type: 'SET_RESULTS_CONFIG_VALUE',
                key: 'helpers.quoteModalRedirect',
                value: `${baseUrl}/${path}`
            });
        }
    };

    /**
     * Gets Occupant Type label
     * @param {String} occupantType Occupant Type
     * @return {String} Returns Occupant Type label
     */
    const getOccupancyType = (occupantType) => ({
        OWNER: 'Home owner',
        TENANT: 'Tenant',
        LANDLORD: 'Landlord'
    }[occupantType]);

    /**
     * Gets Product Category Type label
     * @param {String} occupantType Occupant Type
     * @param {String} productCategory Product Cateogry
     * @return {String} Returns Product Category Type label
     */
    const getProductCategory = (occupantType, productCategory) => {
        if (!occupantType || !productCategory) {
            return '';
        }

        const isLandlord = occupantType === 'LANDLORD';
        const homeText = isLandlord ? 'Building' : 'Home';

        return {
            H: `${homeText} Only Cover`,
            C: 'Contents Only Cover',
            HC: `${homeText} & Contents`
        }[productCategory];
    };

    /**
     * Gets Yes/No label
     * @param {String} value Y or N value
     * @return {String} Returns Yes/No label
     */
    const getYesNo = (value) => ({
        Y: 'Yes',
        N: 'No'
    }[value]);

    /**
     * Gets Previous Insurer label
     * @param {String} insurer Previous Insurer
     * @return {String} Returns Previous Insurer label
     */
    const getPreviousInsurer = (insurer) => {
        if (insurer) {
            return `Previously insured with ${insurer}`;
        }

        return '';
    };

    /**
     * Gets Previous Claims label
     * @param {String} claims Previous Claims
     * @return {String} Returns Previous Claims label
     */
    const getPreviousClaims = (claims) => ({
        Y: 'Have had claims in last 5 years',
        N: 'No claims in last 5 years'
    }[claims]);

    /**
     * Gets Alarm System label
     * @param {String} alarm Alarm System
     * @return {String} Returns Alarm System label
     */
    const  getAlarmSystem = (alarm) => ({
        Y: 'Has alarm system(s)',
        N: 'No alarm systems'
    }[alarm]);

    /**
     * Gets Body Corporate label
     * @param {String} bodyCorporate Body Corporate
     * @return {String} Returns Body Corporate label
     */
    const getBodyCorporate = (bodyCorporate) => ({
        Y: 'Body corp / Strata title',
        N: 'No body corp / Strata title'
    }[bodyCorporate]);

    /**
     * Gets Year Range Format label
     * @param {String} year Year Range Format
     * @return {String} Returns Year Range Format label
     */
    const getYearRangeFormat = (year) => ({
        2030: '2030',
        2029: '2029',
        2028: '2028',
        2027: '2027',
        2026: '2026',
        2025: '2025',
        2024: '2024',
        2023: '2023',
        2022: '2022',
        2021: '2021',
        2020: '2020',
        2019: '2019',
        2018: '2018',
        2017: '2017',
        2016: '2016',
        2015: '2015',
        2014: '2014',
        2013: '2013',
        2012: '2012',
        2011: '2011',
        2010: '2011+',
        2000: '2000 - 2010',
        1990: '1990 - 1999',
        1980: '1980 - 1989',
        1970: '1970 - 1979',
        1960: '1960 - 1969',
        1945: '1945 - 1959',
        1940: '1940 - 1944',
        1914: '1914 - 1939',
        1900: '1900 - 1913',
        1891: '1891 - 1899',
        1840: '1840 - 1890',
        1839: 'Pre 1840'
    }[year]);

    /**
     * Gets Wall Material label
     * @param {String} material Wall Material
     * @return {String} Returns Wall Material label
     */
    const getWallMaterial = (material) => ({
        CLADDING: 'Cladding',
        BESSER_BLOCK_CEMENT: 'Besser block / cement',
        BRICK_VENEER: 'Brick veneer',
        BRICK: 'Double Brick',
        FIBRO: 'Non-Asbestos Fibro',
        FIBRO_ASBESTOS: 'Asbestos Fibro',
        STONE: 'Stone',
        WEATHERBOARD: 'Weatherboard',
        OTHER: 'Other'
    }[material]);

    /**
     * Gets Roof Material label
     * @param {String} material Roof Material
     * @return {String} Returns Roof Material label
     */
    const getRoofMaterial = (material) => ({
        CEMENT_TILES: 'Cement tiles',
        CLAY_TERRACOTTA_TILES: 'Clay / terracotta tiles',
        COLOURBOND: 'Colorbond',
        METAL_OTHER: 'Metal other',
        SLATE: 'Slate',
        ASBESTOS: 'Asbestos',
        OTHER: 'Other'
    }[material]);

    /**
     * Gets Property Type label
     * @param {String} type Property Type
     * @return {String} Returns Property Type label
     */
    const getPropertyType = (type) => ({
        APARTMENT: 'Apartment',
        DUPLEX: 'Duplex',
        FLAT: 'Flat',
        FREESTANDING_HOME: 'Freestanding home',
        HOME_UNIT: 'Home unit',
        SEMI_DETACHED_HOUSE: 'Semi-detached house',
        TERRACE_HOUSE: 'Terrace house',
        TOWNHOUSE: 'Townhouse',
        VILLA: 'Villa',
        BOARDING_HOUSE: 'Boarding house',
        CARAVAN: 'Caravan',
        DORMITORY: 'Dormitory',
        GRANNY_FLAT: 'Granny flat',
        MOBILE_HOME: 'Mobile home',
        SHED: 'Shed',
        SHIPPING_CONTAINER: 'Shipping container',
        STORAGE_FACILITY: 'Storage facility',
        WAREHOUSE: 'Warehouse',
        OTHER: 'Other'
    }[type]);

    /**
     * Gets Managed By label
     * @param {String} managedBy Managed By
     * @return {String} Returns Managed By label
     */
    const getManagedBy = (managedBy) => ({
        REAL_ESTATE: 'Real Estate Agent or Property Manager',
        SELF_MANAGED: 'Self managed',
        OTHER: 'Other'
    }[managedBy]);

    /**
     * Gets Occupied By label
     * @param {String} occupiedBy Occupied By
     * @return {String} Returns Occupied By label
     */
    const getOccupiedBy = (occupiedBy) => ({
        RENTED_TO_TENANTS: 'Rented to tenants',
        HOLIDAY_HOME_RENTED_OUT: 'Holiday home (may be rented out)',
        HOLIDAY_HOME_NOT_RENTED_OUT: 'Holiday home (not rented out)',
        UNOCCUPIED: 'Unoccupied',
        OTHER: 'Other'
    }[occupiedBy]);

    /**
     * Gets Rental Agreement label
     * @param {String} hasAgreement Rental Agreement
     * @return {String} Returns Rental Agreement label
     */
    const getRentalAgreement = (hasAgreement) => ({
        Y: 'Have a written rental agreement in place',
        N: 'No written rental agreement in place'
    }[hasAgreement]);

    /**
     * Gets Moved In Year label
     * @param {String} year Moved In Year
     * @return {String} Returns Moved In Year label
     */
    const getMovedInYear = (year) => {
        if (!year || year === 'NotAtThisAddress') {
            return 'Not yet living at this address';
        }

        return year;
    };

    /**
     * Renders the Cover Details Component
     * @return {Node} Returns a CoverDetails Component based on the PropTypes
     */
    const getCoverDetails = () => {
        const { occupancyType, productCategory, suburb } = props;

        const occupancyTypeText = getOccupancyType(occupancyType);
        const productCategoryText = getProductCategory(occupancyType, productCategory);

        return <CoverDetails baseUrl={baseUrl} occupancyType={occupancyTypeText} productCategory={productCategoryText} suburb={suburb} onClick={quoteModalOnClick} getHref={getHref} />;
    };

    /**
     * Renders the Occupancy Details Component
     * @return {Node} Returns a OccupancyDetails Component based on the PropTypes
     */
    const getOccupancyDetails = () => {
        const { occupancyType, hasMortgage, howOccupied, movedInMonth, movedInYear, isPrincipalResidence,
            hasBusinessActivity, managedBy, hasValidRentalLease, weeklyRentalValue } = props;

        const hasMortgageText = occupancyType !== 'TENANT' ? getYesNo(hasMortgage) : '';
        const monthText = capitalize(movedInMonth);
        const movedInYearText = getMovedInYear(movedInYear);
        const isPrincipalResidenceText = getYesNo(isPrincipalResidence);
        const businessText = getYesNo(hasBusinessActivity);
        const occupiedText = occupancyType === 'LANDLORD' ? getOccupiedBy(howOccupied) : '';
        const managedByText = getManagedBy(managedBy);
        const rentalLeaseText = occupancyType === 'LANDLORD' ? getRentalAgreement(hasValidRentalLease) : '';
        const weeklyRentText = weeklyRentalValue ? masks.currency(weeklyRentalValue, ',') : '';

        return (
            <OccupancyDetails
                hasMortgage={hasMortgageText}
                howOccupied={occupiedText}
                movedInMonth={monthText}
                movedInYear={movedInYearText}
                isPrincipalResidence={isPrincipalResidenceText}
                hasBusinessActivity={businessText}
                managedBy={managedByText}
                hasValidRentalLease={rentalLeaseText}
                weeklyRentalValue={weeklyRentText}
                onClick={quoteModalOnClick}
                getHref={getHref}
            />
        );
    };

    /**
     * Renders the Description Details Component
     * @return {Node} Returns a DescriptionDetails Component based on the PropTypes
     */
    const getDescriptionDetails = () => {
        const { propertyType, wallMaterial, roofMaterial, yearBuilt, hasBodyCorporate, hasSecurityAlarm } = props;

        const propertyTypeText = getPropertyType(propertyType);
        const wallMaterialText = getWallMaterial(wallMaterial);
        const roofMaterialText = getRoofMaterial(roofMaterial);
        const bodyCorporateText = getBodyCorporate(hasBodyCorporate);
        const securityAlarmText = getAlarmSystem(hasSecurityAlarm);
        const yearBuiltText = getYearRangeFormat(yearBuilt);

        return (
            <DescriptionDetails
                propertyType={propertyTypeText}
                wallMaterial={wallMaterialText}
                roofMaterial={roofMaterialText}
                yearBuilt={yearBuiltText}
                hasBodyCorporate={bodyCorporateText}
                hasSecurityAlarm={securityAlarmText}
                onClick={quoteModalOnClick}
                getHref={getHref}
            />
        );
    };

    /**
     * Renders the Cover Amounts Details Component
     * @return {Node} Returns a CoverAmountsDetails Component based on the PropTypes
     */
    const getCoverAmounts = () => {
        const {
            occupancyType,
            hasPortableItems,
            totalRebuildCost,
            totalContentReplacementCost,
            totalUnspecifiedPortableItems,
            bicyclesCover,
            jewelryWatchesCover,
            clothingCover,
            sportingEquipmentCover,
            musicalInstrumentsCover,
            photographicEquipmentCover
        } = props;

        const hasPortableItemsFlag = hasPortableItems === 'Y';
        const totalRebuildText = occupancyType !== 'TENANT' && totalRebuildCost ? masks.currency(totalRebuildCost, ',') : '';
        const contentReplacementText = totalContentReplacementCost ? masks.currency(totalContentReplacementCost, ',') : '';
        const unspecifiedItemsText = hasPortableItemsFlag && totalUnspecifiedPortableItems ? masks.currency(totalUnspecifiedPortableItems, ',') : '';
        const bicycleText = hasPortableItemsFlag && bicyclesCover ? masks.currency(bicyclesCover, ',') : '';
        const jewelText = hasPortableItemsFlag && jewelryWatchesCover ? masks.currency(jewelryWatchesCover, ',') : '';
        const clothingText = hasPortableItemsFlag && clothingCover ? masks.currency(clothingCover, ',') : '';
        const sportText = hasPortableItemsFlag && sportingEquipmentCover ? masks.currency(sportingEquipmentCover, ',') : '';
        const musicText = hasPortableItemsFlag && musicalInstrumentsCover ? masks.currency(musicalInstrumentsCover, ',') : '';
        const photoText = hasPortableItemsFlag && photographicEquipmentCover ? masks.currency(photographicEquipmentCover, ',') : '';
        const portableText = !hasPortableItemsFlag ? 'None' : '';

        return (
            <CoverAmountsDetails
                totalRebuildCost={totalRebuildText}
                totalContentReplacementCost={contentReplacementText}
                totalUnspecifiedPortableItems={unspecifiedItemsText}
                bicyclesCover={bicycleText}
                jewelryWatchesCover={jewelText}
                clothingCover={clothingText}
                sportingEquipmentCover={sportText}
                musicalInstrumentsCover={musicText}
                photographicEquipmentCover={photoText}
                portableItems={portableText}
                onClick={quoteModalOnClick}
                getHref={getHref}
            />
        );
    };

    /**
     * Renders the Previous Insurance Component
     * @return {Node} Returns a PreviousInsurance Component based on the PropTypes
     */
    const getPreviousInsurance = () => {
        const { previousInsurer, hasPreviousClaims } = props;

        const previousText = getPreviousInsurer(previousInsurer);
        const previousClaims = getPreviousClaims(hasPreviousClaims);

        return <PreviousInsurance previousInsurer={previousText} previousClaims={previousClaims} onClick={quoteModalOnClick} getHref={getHref} />;
    };

    /**
     * Renders the Policy Holder Component
     * @return {Node} Returns a PolicyHolderDetails Component based on the PropTypes
     */
    const getPolicyHolder = () => {
        const { policyHolderFirstName, policyHolderLastName, policyHolderDob } = props;

        const policyHolderName = `${policyHolderFirstName} ${policyHolderLastName}`;
        const policyHolderTransformedDob = transforms.displayDate({ value: policyHolderDob });

        return <PolicyHolderDetails policyHolderName={policyHolderName} policyHolderDob={policyHolderTransformedDob} onClick={quoteModalOnClick} getHref={getHref} />;
    };

    /**
     * Renders the Commencement date Component
     * @return {Node} Returns a CommencementDate Component based on the PropTypes
     */
    const getCommencementDate = () => {
        const { commencementDate } = props;

        const transformedDate = transforms.displayDate({ value: commencementDate });

        return <CommencementDate commencementDate={transformedDate} onClick={quoteModalOnClick} getHref={getHref} />;
    };

    /**
     * Render the Edit My Details Component
     * @return {Node} Renders the Edit My Details Component
     */
    return (
        <div>
            {getCoverDetails()}
            {getOccupancyDetails()}
            {getDescriptionDetails()}
            {getCoverAmounts()}
            {getPreviousInsurance()}
            {getPolicyHolder()}
            {getCommencementDate()}
        </div>
    );
};

EditMyDetails.propTypes = {
    occupancyType: PropTypes.oneOf(['OWNER', 'TENANT', 'LANDLORD']),
    productCategory: PropTypes.oneOf(['H', 'C', 'HC']),
    suburb: PropTypes.string,
    hasMortgage: PropTypes.oneOf(['Y', 'N']),
    movedInMonth: PropTypes.string,
    movedInYear: PropTypes.string,
    isPrincipalResidence: PropTypes.oneOf(['Y', 'N']),
    hasBusinessActivity: PropTypes.oneOf(['Y', 'N']),
    managedBy: PropTypes.oneOf(['REAL_ESTATE', 'SELF_MANAGED', 'OTHER']),
    howOccupied: PropTypes.oneOf(['RENTED_TO_TENANTS', 'HOLIDAY_HOME_RENTED_OUT', 'HOLIDAY_HOME_NOT_RENTED_OUT', 'UNOCCUPIED', 'OTHER']),
    hasValidRentalLease: PropTypes.oneOf(['Y', 'N']),
    weeklyRentalValue: PropTypes.string,
    propertyType: PropTypes.string,
    wallMaterial: PropTypes.string,
    roofMaterial: PropTypes.string,
    yearBuilt: PropTypes.string,
    hasBodyCorporate: PropTypes.oneOf(['Y', 'N']),
    hasSecurityAlarm: PropTypes.oneOf(['Y', 'N']),
    totalRebuildCost: PropTypes.string,
    totalContentReplacementCost: PropTypes.string,
    totalUnspecifiedPortableItems: PropTypes.string,
    hasPortableItems: PropTypes.string,
    bicyclesCover: PropTypes.string,
    jewelryWatchesCover: PropTypes.string,
    clothingCover: PropTypes.string,
    sportingEquipmentCover: PropTypes.string,
    musicalInstrumentsCover: PropTypes.string,
    photographicEquipmentCover: PropTypes.string,
    previousInsurer: PropTypes.string,
    hasPreviousClaims: PropTypes.oneOf(['Y', 'N']),
    policyHolderFirstName: PropTypes.string,
    policyHolderLastName: PropTypes.string,
    policyHolderDob: PropTypes.string,
    commencementDate: PropTypes.string,
    dispatch: PropTypes.func
};

export { EditMyDetails };
